/* eslint @typescript-eslint/unbound-method: "warn" */

import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Option } from '@fmnts/core';
import { BezierCurve, Vector2d, Vector2dLike } from '@fmnts/core/math';
import { throttle } from './throttle';

export interface SignatureData {
  data: any;
  svg: any;
}
export type SignatureValue = Option<SignatureData>;

interface IPoint extends Vector2dLike {
  time?: number;
}

class Point extends Vector2d implements IPoint {
  constructor(
    x: number,
    y: number,
    public time = Date.now(),
  ) {
    super(x, y);
  }

  public velocityFrom(other: Point): number {
    return this.time !== other.time
      ? Vector2d.distance(this, other) / (this.time - other.time)
      : 0;
  }
}

class BezierCurvePath extends BezierCurve {
  public static withWidths(
    curve: BezierCurve,
    widths: { start: number; end: number },
  ) {
    return new BezierCurvePath(curve.c1, curve.c2, curve.c3, curve.c4, widths);
  }

  constructor(
    c1: Vector2d,
    c2: Vector2d,
    c3: Vector2d,
    c4: Vector2d,
    public readonly widths: { start: number; end: number },
  ) {
    super(c1, c2, c3, c4);
  }
}

@Directive({
  selector: '[nextSignaturePad]',
  standalone: true,
})
export class SignaturePadDirective implements OnDestroy, OnInit {
  @HostBinding('class') componentClass = 'signature-pad';

  @Input() backgroundColor = 'white';
  @Input() drawColor = 'black';
  @Input() maxWidth = 3;
  @Input() minDistance = 5;
  @Input() minWidth = 1;

  @Output() update = new EventEmitter<SignatureValue>();

  public isEmpty = true;

  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private currentCurvePoints: Point[];
  private currentVelocity: number;
  private currentWidth: number;
  private data: Point[][];
  private inputStarted = false;
  private velocityWeight: number;

  @HostListener('window:resize') handleResize() {
    this.resizeCanvas();
  }

  private get dotSize(): number {
    return (this.minWidth + this.maxWidth) / 2.0;
  }

  public constructor(private element: ElementRef) {
    this.canvas = this.element.nativeElement;
    this.context = this.canvas.getContext('2d');

    this.data = [];
    this.velocityWeight = 0.7;

    // Bind functions for event listeners and throttle updates to 60 per second
    this.handleInputDown = this.handleInputDown.bind(this);
    this.handleInputMove = throttle(this.handleInputMove.bind(this), 16);
    this.handleInputUp = this.handleInputUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = throttle(this.handleTouchMove.bind(this), 16);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  ngOnInit() {
    // We want to disable pinching and dragging on the component
    this.canvas.style.touchAction = 'none';

    this.initEventListeners();
    this.resizeCanvas();
  }

  ngOnDestroy() {
    this.destroyEventListeners();
  }

  /**
   * Refresh canvas
   *
   * When status of the signature control is changed due to conditionals,
   * the canvas should be drawn again in order to render the signature.
   */
  public refresh() {
    this.resizeCanvas();
  }

  private initEventListeners() {
    if ('PointerEvent' in window) {
      this.initPointerEventListeners();
    } else {
      this.initMouseEventListeners();

      if ('ontouchstart' in window) {
        this.initTouchEventListeners();
      }
    }
  }

  private destroyEventListeners() {
    this.canvas.removeEventListener('pointerdown', this.handleInputDown);
    this.canvas.removeEventListener('pointermove', this.handleInputMove);
    document.removeEventListener('pointerup', this.handleInputUp);

    this.canvas.removeEventListener('mousedown', this.handleInputDown);
    this.canvas.removeEventListener('mousemove', this.handleInputMove);
    document.removeEventListener('mouseup', this.handleInputUp);

    this.canvas.removeEventListener('touchstart', this.handleTouchStart);
    this.canvas.removeEventListener('touchmove', this.handleTouchMove);
    this.canvas.removeEventListener('touchend', this.handleTouchEnd);
  }

  private initPointerEventListeners() {
    this.canvas.addEventListener('pointerdown', this.handleInputDown);
    this.canvas.addEventListener('pointermove', this.handleInputMove);
    document.addEventListener('pointerup', this.handleInputUp);
  }

  private initMouseEventListeners() {
    this.canvas.addEventListener('mousedown', this.handleInputDown);
    this.canvas.addEventListener('mousemove', this.handleInputMove);
    document.addEventListener('mouseup', this.handleInputUp);
  }

  private initTouchEventListeners() {
    this.canvas.addEventListener('touchstart', this.handleTouchStart);
    this.canvas.addEventListener('touchmove', this.handleTouchMove);
    this.canvas.addEventListener('touchend', this.handleTouchEnd);
  }

  private handleInputDown(event: MouseEvent | PointerEvent) {
    if (event.button === 0) {
      this.inputStarted = true;
      this.beginDrawing(event.clientX, event.clientY);
    }
  }

  private handleInputMove(event: MouseEvent | PointerEvent) {
    if (this.inputStarted) {
      this.inputStarted = true;
      this.updateDrawing(event.clientX, event.clientY);
    }
  }

  private handleInputUp(event: MouseEvent | PointerEvent) {
    if (event.button === 0 && this.inputStarted) {
      this.inputStarted = false;
      this.endDrawing(event.clientX, event.clientY);
    }
  }

  private handleTouchStart(event: TouchEvent) {
    event.preventDefault();

    if (event.targetTouches.length === 1) {
      const touch = event.changedTouches[0];
      this.beginDrawing(touch.clientX, touch.clientY);
    }
  }

  private handleTouchMove(event: TouchEvent) {
    event.preventDefault();

    const touch = event.changedTouches[0];
    this.updateDrawing(touch.clientX, touch.clientY);
  }

  private handleTouchEnd(event: TouchEvent) {
    if (event.target === this.canvas) {
      event.preventDefault();

      const touch = event.changedTouches[0];
      this.endDrawing(touch.clientX, touch.clientY);
    }
  }

  private resizeCanvas() {
    // We need to wait for the next invocation of the run loop
    setTimeout(() => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      this.canvas.width = this.canvas.offsetWidth * ratio;
      this.canvas.height = this.canvas.offsetHeight * ratio;
      this.context.scale(ratio, ratio);

      // Reload the data after resizing
      this.fromData(this.data);
    });
  }

  private resetCanvas() {
    this.currentCurvePoints = [];
    this.currentVelocity = 0;
    this.currentWidth = (this.minWidth + this.maxWidth) / 2.0;
    this.context.fillStyle = this.drawColor;
  }

  public clear() {
    this.context.fillStyle = this.backgroundColor;
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.data = [];
    this.resetCanvas();
    this.isEmpty = true;
  }

  private beginDrawing(x: number, y: number, time?: number) {
    this.data.push([]);
    this.resetCanvas();
    this.updateDrawing(x, y, time);
  }

  private updateDrawing(x: number, y: number, time?: number) {
    const point = this.transformPointToCanvas(x, y, time);

    // Check whether data has an element, if not it means beginDrawing() was not called
    if (this.data.length === 0) {
      throw new Error(
        'Cannot update drawing \n' + 'Expected begin event to be fired first',
      );
    }
    const currentPointGroup = this.data[this.data.length - 1];
    const lastPoint =
      currentPointGroup.length > 0
        ? currentPointGroup[currentPointGroup.length - 1]
        : null;
    const shouldAddNewPoint = lastPoint
      ? Vector2d.distance(point, lastPoint) > this.minDistance
      : true;

    if (shouldAddNewPoint) {
      const curve = this.createCurve(point);

      if (!lastPoint) {
        this.drawDot(point);
      } else if (curve) {
        this.drawCurve(curve);
      }

      currentPointGroup.push(new Point(point.x, point.y, point.time));
    }
  }

  private endDrawing(x: number, y: number) {
    this.updateDrawing(x, y);

    if (this.data.length > 0) {
      this.update.emit(this.toData());
    }
  }

  // Transform the point from global to the canvas coordinate system
  private transformPointToCanvas(x: number, y: number, time?: number): Point {
    const rect = this.canvas.getBoundingClientRect();

    return new Point(x - rect.left, y - rect.top, time ? time : Date.now());
  }

  private createCurve(point: Point): BezierCurvePath {
    const points = this.currentCurvePoints;

    points.push(point);

    if (points.length <= 2) {
      return null;
    }

    // We need four points to render a Bezier curve, to make initial render
    // faster, we can duplicate the first point, if there are only three.
    if (points.length === 3) {
      points.unshift(points[0]);
    }

    const curve = BezierCurvePath.withWidths(
      BezierCurvePath.fromPoints(points),
      this.calculateCurveWidths(points[1], points[2]),
    );

    // Remove the first point, to only have four points at max.
    points.shift();

    return curve;
  }

  private calculateCurveWidths(
    c1: Point,
    c2: Point,
  ): { start: number; end: number } {
    const velocity =
      this.velocityWeight * c2.velocityFrom(c1) +
      (1 - this.velocityWeight) * this.currentVelocity;

    const width = Math.max(this.maxWidth / (1 + velocity), this.minWidth);
    const widths = { start: this.currentWidth, end: width };

    this.currentVelocity = velocity;
    this.currentWidth = width;

    return widths;
  }

  // Render a single dot (used, when there's only one point for a draw)
  private drawDot(point: Point, color = 'black') {
    this.context.beginPath();

    this.drawCurveSegment(point.x, point.y, this.dotSize);

    this.context.closePath();
    this.context.fillStyle = color;
    this.context.fill();
  }

  // Renders a curve by interpolating and approximating the points on the Bezier
  // curve as single points.
  private drawCurve(curve: BezierCurvePath) {
    const steps = 2 * Math.floor(curve.length());
    const widthDelta = curve.widths.end - curve.widths.start;

    this.context.beginPath();

    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const point = curve.point(t);

      // Also interpolate the width, by using t^3 as a ease-in/out weight
      const width = Math.min(
        curve.widths.start + t * t * t * widthDelta,
        this.maxWidth,
      );

      this.drawCurveSegment(point.x, point.y, width);
    }

    this.context.closePath();
    this.context.fillStyle = this.drawColor;
    this.context.fill();
  }

  private drawCurveSegment(x: number, y: number, width: number) {
    this.context.moveTo(x, y);
    this.context.arc(x, y, width, 0, 2 * Math.PI, false);

    this.isEmpty = false;
  }

  public fromData(groups: IPoint[][] | undefined) {
    if (!groups) {
      return this.clear();
    }

    // Ensure to map point like structure to point instances
    this.data = groups.map((group) =>
      group.map((point) => new Point(point.x, point.y, point.time)),
    );
    this.renderData(
      this.data,
      this.drawCurve.bind(this),
      this.drawDot.bind(this),
    );
  }

  private toData() {
    return {
      data: this.data,
      svg: this.toSVG(),
    };
  }

  // Renders data, using custom draw functions. Changing the draw function makes
  // it possible to either generate an SVG or re-draw on the canvas.
  private renderData(
    groups: Point[][],
    drawCurve: (curve: BezierCurvePath) => void,
    drawDot: (point: Point) => void,
  ) {
    for (const points of groups) {
      if (points.length > 1) {
        for (let j = 0; j < points.length; j += 1) {
          const point = new Point(points[j].x, points[j].y, points[j].time);

          if (j === 0) {
            this.resetCanvas();
          }

          const curve = this.createCurve(point);

          if (curve) {
            drawCurve(curve);
          }
        }
      } else {
        this.resetCanvas();

        drawDot(points[0]);
      }
    }
  }

  private toSVG(): string {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const minX = 0;
    const minY = 0;
    const maxX = this.canvas.width / ratio;
    const maxY = this.canvas.height / ratio;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('width', this.canvas.width.toString());
    svg.setAttribute('height', this.canvas.height.toString());

    this.renderData(
      this.data,
      (curve) => {
        const path = document.createElement('path');

        // Need to check curve for NaN values, these pop up when drawing
        // lines on the canvas that are not continuous. E.g. Sharp corners
        // or stopping mid-stroke and than continuing without lifting mouse.
        if (
          !isNaN(curve.c2.x) &&
          !isNaN(curve.c2.y) &&
          !isNaN(curve.c3.x) &&
          !isNaN(curve.c3.y)
        ) {
          const attr =
            `M ${curve.c1.x.toFixed(3)},${curve.c1.y.toFixed(3)} ` +
            `C ${curve.c2.x.toFixed(3)},${curve.c2.y.toFixed(3)} ` +
            `${curve.c3.x.toFixed(3)},${curve.c3.y.toFixed(3)} ` +
            `${curve.c4.x.toFixed(3)},${curve.c4.y.toFixed(3)}`;
          path.setAttribute('d', attr);
          path.setAttribute(
            'stroke-width',
            (curve.widths.end * 2.25).toFixed(3),
          );
          path.setAttribute('stroke', 'black');
          path.setAttribute('fill', 'none');
          path.setAttribute('stroke-linecap', 'round');

          svg.appendChild(path);
        }
      },
      (point) => {
        const circle = document.createElement('circle');
        circle.setAttribute('r', this.dotSize.toString());
        circle.setAttribute('cx', point.x.toString());
        circle.setAttribute('cy', point.y.toString());
        circle.setAttribute('fill', 'black');

        svg.appendChild(circle);
      },
    );

    const prefix = 'data:image/svg+xml;base64,';
    const header =
      '<svg' +
      ' xmlns="http://www.w3.org/2000/svg"' +
      ' xmlns:xlink="http://www.w3.org/1999/xlink"' +
      ` viewBox="${minX} ${minY} ${maxX} ${maxY}"` +
      ` width="${maxX}"` +
      ` height="${maxY}"` +
      '>';
    let body = svg.innerHTML;

    // IE hack for missing innerHTML property on SVGElement
    if (body === undefined) {
      const dummy = document.createElement('dummy');
      const nodes = svg.childNodes;
      dummy.innerHTML = '';

      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < nodes.length; i += 1) {
        dummy.appendChild(nodes[i].cloneNode(true));
      }

      body = dummy.innerHTML;
    }

    const footer = '</svg>';
    const data = header + body + footer;

    return prefix + btoa(data);
  }
}
